import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
  signUp,
  login,
  forgetPassword,
  emailVerification,
  otpVerification,
  resetPassword,
  updatePassword,
  logout,
} from "../../services/authService";
import toast from "../../utils/toast";

const initialState = {
  signUpData: null,
  loginData: null,
  forgetPasswordData: null,
  forgetPasswordResponse: null,
  updatePasswordData: null,
  tokenData: null,
  email: null,
  emailData: null,
  otpToken: null,
  resetData: null,
  fcmToken: null,
  loading: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    setSignUpData: (state, action) => {
      state.signUpData = action.payload;
    },

    setLoginData: (state, action) => {
      state.loginData = action.payload;
    },

    setUserData: (state, action) => {
      state.tokenData = action.payload;
    },

    setForgetPasswordData: (state, action) => {
      state.forgetPasswordData = action.payload;
    },

    setEmail: (state, action) => {
      state.email = action.payload.email;
    },

    setFcmToken: (state, action) => {
      state.fcmToken = action.payload.fcmToken;
    },
    clearAuth: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(signUp.fulfilled, (state, action) => {
      if (action?.payload?.success == true) {
        state.loading = false;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(login.fulfilled, (state, action) => {
      if (action?.payload?.success == true) {
        state.loading = false;
        state.tokenData = action.payload?.data;
      } else {
        // Show toast only for pending-approval style responses; inline for others
        state.loading = false;
        const msg = action?.payload?.message || "";
        if (typeof msg === "string" && msg.toLowerCase().includes("pending")) {
          toast.error(msg);
        }
      }
    });

    builder.addCase(forgetPassword.fulfilled, (state, action) => {
      if (action?.payload?.success == true) {
        state.loading = false;
        state.forgetPasswordResponse = action.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(emailVerification.fulfilled, (state, action) => {
      if (action?.payload?.success === true) {
        state.emailData = action?.payload;
      } else {
        toast.error(action?.payload?.message);
      }
      state.loading = false;
    });

    builder.addCase(otpVerification.fulfilled, (state, action) => {
      if (action?.payload?.success == true) {
        state.loading = false;
        state.otpToken = action.payload?.data;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(resetPassword.fulfilled, (state, action) => {
      if (action?.payload?.success == true) {
        state.loading = false;
        state.resetData = action.payload?.data;
        toast.success(action?.payload?.message, {
          style: {
            background: "#E6F7FB",
            color: "#007EA7",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            padding: "12px 16px",
          },
          progressStyle: {
            background: "#007EA7",
          },
          icon: "✅",
        });
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder.addCase(updatePassword.fulfilled, (state, action) => {
      state.loading = false;

      if (action.payload?.status >= 200 && action.payload?.status < 300) {
        toast.success(action?.payload?.message, {
          style: {
            background: "#E6F7FB",
            color: "#007EA7",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
            padding: "12px 16px",
          },
          progressStyle: {
            background: "#007EA7",
          },
          icon: "✅",
        });
      }
      else {
        toast.error(action?.payload?.message);
      }
    });


    builder.addCase(logout.fulfilled, (state, action) => {
      if (action?.payload?.success == true) {
        state.loading = false;
        state.signUpData = null;
        state.email = null;
        state.emailData = null;
        state.forgetPasswordData = null;
        state.forgetPasswordResponse = null;
        state.loginData = null;
        state.otpToken = null;
        state.resetData = null;
        state.tokenData = null;
        state.fcmToken = null;
      } else {
        state.loading = false;
        toast.error(action?.payload?.message);
      }
    });

    builder
      .addMatcher(
        isAnyOf(
          signUp?.pending,
          login?.pending,
          forgetPassword?.pending,
          emailVerification?.pending,
          otpVerification?.pending,
          resetPassword?.pending,
          updatePassword?.pending,
          logout?.pending
        ),
        (state, action) => {
          if (action?.meta?.arg?.silent === true) return;
          state.loading = true;
        }
      )
      .addMatcher(
        isAnyOf(
          signUp?.rejected,
          login?.rejected,
          forgetPassword?.rejected,
          emailVerification?.rejected,
          otpVerification?.rejected,
          resetPassword?.rejected,
          updatePassword?.rejected,
          logout?.rejected
        ),
        (state, action) => {
          state.loading = false;
          // For login rejections, show toast only for pending approval
          if (action?.type?.includes("/login/rejected")) {
            const msg = action?.payload?.message || "";
            if (typeof msg === "string" && msg.toLowerCase().includes("pending")) {
              toast.error(msg);
            }
            return;
          }
          toast.error(action?.payload?.message);
        }
      );
  },
});

export const {
  setSignUpData,
  setLoginData,
  setForgetPasswordData,
  setUserData,
  setEmail,
  setFcmToken,
  clearAuth,
} = authSlice?.actions;
export const getToken = (state) => state?.auth?.tokenData;
export default authSlice?.reducer;
